/**
 * Model Statistics Service
 * 
 * Provides utilities to get statistical information about loaded models
 * in the Autodesk Forge Viewer, including total element counts, categories,
 * and other model metadata.
 */

export interface ModelStatistics {
  totalElements: number;
  leafElements: number;
  totalCategories: number;
  isLoaded: boolean;
  modelName?: string;
  loadedDate?: Date;
}

export class ModelStatisticsService {
  /**
   * Get the total number of elements in the loaded model
   * Uses the instance tree to count all elements including non-leaf nodes
   */
  static async getTotalElementCount(viewer: Autodesk.Viewing.GuiViewer3D): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!viewer || !viewer.model) {
        reject(new Error('Viewer or model not available'));
        return;
      }

      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        // Wait for the tree to be created
        const onTreeCreated = () => {
          viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
          this.getTotalElementCount(viewer).then(resolve).catch(reject);
        };
        viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
        return;
      }

      let elementCount = 0;
      const rootId = tree.getRootId();

      // Recursively count all nodes in the tree
      const countNodes = (nodeId: number) => {
        elementCount++;
        tree.enumNodeChildren(nodeId, (childId) => {
          countNodes(childId);
        }, false);
      };

      try {
        countNodes(rootId);
        // Subtract 1 to exclude the root node itself
        resolve(Math.max(0, elementCount - 1));
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Get the number of leaf elements (selectable elements) in the model
   * These are elements that have no children and can be selected/highlighted
   */
  static async getLeafElementCount(viewer: Autodesk.Viewing.GuiViewer3D): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!viewer || !viewer.model) {
        reject(new Error('Viewer or model not available'));
        return;
      }

      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        // Wait for the tree to be created
        const onTreeCreated = () => {
          viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
          this.getLeafElementCount(viewer).then(resolve).catch(reject);
        };
        viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
        return;
      }

      const leafElements: number[] = [];
      const rootId = tree.getRootId();

      // Recursively find leaf nodes
      const findLeafNodes = (nodeId: number) => {
        const childCount = tree.getChildCount(nodeId);
        if (childCount === 0) {
          // This is a leaf node
          leafElements.push(nodeId);
        } else {
          // This node has children, enumerate them
          tree.enumNodeChildren(nodeId, (childId) => {
            findLeafNodes(childId);
          }, false);
        }
      };

      try {
        findLeafNodes(rootId);
        resolve(leafElements.length);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Get comprehensive model statistics
   */
  static async getModelStatistics(viewer: Autodesk.Viewing.GuiViewer3D): Promise<ModelStatistics> {
    try {
      const [totalElements, leafElements] = await Promise.all([
        this.getTotalElementCount(viewer),
        this.getLeafElementCount(viewer)
      ]);

      // Get model name if available
      const modelName = viewer.model?.getData()?.metadata?.name || 
                       viewer.model?.getData()?.metadata?.['Model Name'] || 
                       'Unknown Model';

      return {
        totalElements,
        leafElements,
        totalCategories: 0, // Could be enhanced to count categories
        isLoaded: true,
        modelName,
        loadedDate: new Date()
      };
    } catch (error) {
      console.error('Error getting model statistics:', error);
      return {
        totalElements: 0,
        leafElements: 0,
        totalCategories: 0,
        isLoaded: false
      };
    }
  }

  /**
   * Get all element IDs in the model (leaf nodes only)
   * Useful for bulk operations on all selectable elements
   */
  static async getAllElementIds(viewer: Autodesk.Viewing.GuiViewer3D): Promise<number[]> {
    return new Promise((resolve, reject) => {
      if (!viewer || !viewer.model) {
        reject(new Error('Viewer or model not available'));
        return;
      }

      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        // Wait for the tree to be created
        const onTreeCreated = () => {
          viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
          this.getAllElementIds(viewer).then(resolve).catch(reject);
        };
        viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onTreeCreated);
        return;
      }

      const allElementIds: number[] = [];
      const rootId = tree.getRootId();

      // Recursively collect all leaf node IDs
      const collectLeafIds = (nodeId: number) => {
        const childCount = tree.getChildCount(nodeId);
        if (childCount === 0) {
          // This is a leaf node - add to collection
          allElementIds.push(nodeId);
        } else {
          // This node has children, enumerate them
          tree.enumNodeChildren(nodeId, (childId) => {
            collectLeafIds(childId);
          }, false);
        }
      };

      try {
        collectLeafIds(rootId);
        resolve(allElementIds);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Format element count for display
   */
  static formatElementCount(count: number): string {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  }
}